import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useProducts from "hooks/useProducts";
import { FC, useEffect, useRef, useState, memo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AmountTag from "components/tags/amountTag";
import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import { ReactComponent as BarcodeIcon } from "assets/icons/barcode.svg";
import Callout from "components/callout/callout";
import useCategories from "hooks/useCategories";
import useLocations from "hooks/useLocations";
import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import TableList from "components/list/tableList/tableList";
import TableListRow from "components/list/tableList/tableListRow";
import TableListCell from "components/list/tableList/tableListCell";
import { ElementPosition, FormStatus } from "@/types";
import Doc from "@/components/list/docList/doc";
import Image from "@/components/image/Image";
import { APP_CONFIG } from "@/constants/constants";
import LoadMore from "@/components/list/loadMore";
import SearchResultHolder from "./SearchResultHolder";
import IconButton from "@/components/buttons/iconButton";
import useLayout from "@/hooks/useLayout";
import { fetchBarcodeMapping } from "@/hooks/useBarcodes";
import { getProductDoc } from "@/services/firebaseApi";
import { setBarcodeScanHandler } from "@/utils/barcodeScanHandler";

interface SeachFieldProps {
    storageId?: string
    className?: string
    selectedProductCode?: string
    onProductSelect?: (productId: string) => void
    placeholder?: string,
    inlineResults?: boolean
}

const ProductSelector: FC<SeachFieldProps> = ({ 
    storageId,
    className, 
    selectedProductCode,
    onProductSelect,
    placeholder,
    inlineResults = false
}) => {

    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { openDialog, closeDialog, addToast } = useLayout();

    const [inFocus, setInFocus] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const ProductSelectorWrapperRef = useRef<HTMLDivElement>(null);
    const ProductSelectorResultsRef = useRef<HTMLDivElement>(null);
    const [inputLocation, setInputLocation] = useState<ElementPosition | null>(null);

    const {
        filteredProducts,
        filterQuery,
        setFilterQuery,
        products,
        hasNextPage,
        fetchNextPage,
        productsStatus,
        queryMatchingAttributes
    } = useProducts({ storageId });

    // Filter out draft products when in selector mode (for actions)
    const selectableProducts = filteredProducts.filter(product => !product.isDraft)

    const { getCategoryNamesString, findCategory } = useCategories({ storageId })
    const { getLocationNamesString } = useLocations({ storageId })

    // Get selected product for display in selector mode
    const selectedFromList = selectedProductCode ? products?.find(product => product.id === selectedProductCode) : undefined
    const { data: selectedFromCache } = useQuery({
        queryKey: ['product', storageId, selectedProductCode],
        queryFn: () => getProductDoc({ storageId: storageId!, productId: selectedProductCode! }),
        enabled: !!(storageId && selectedProductCode) && !selectedFromList,
        staleTime: 5 * 60 * 1000,
    })
    const selectedProduct = selectedFromList || selectedFromCache

    // Check if any filtered product has location or categories (only if there's a query)
    const hasLocation = filterQuery.length > 0 && selectableProducts.some(product =>
        (product.locations && product.locations.length > 0) || (product.location && product.location.trim() !== '')
    );
    const hasCategories = filterQuery.length > 0 && selectableProducts.some(product => product.categories && product.categories.length > 0);

    const handleOnClick = () => {
        setInFocus(true);
        if (ProductSelectorWrapperRef.current) {
            const rect = ProductSelectorWrapperRef.current.getBoundingClientRect();
            setInputLocation({
                top: rect.bottom,
                left: rect.left,
                width: rect.width
            });
        }
    }
    const handleOnBlur = () => {
        setInFocus(false);
    }
    const handleSearchIconClick = () => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }

    const location = useLocation();

    useEffect(() => {
        handleOnBlur();
        // eslint-disable-next-line
    }, [location]);

    // Update rect on window resize if inFocus
    useEffect(() => {
        if (!inFocus) return;
        const updateRect = () => {
            if (ProductSelectorWrapperRef.current) {
                const rect = ProductSelectorWrapperRef.current.getBoundingClientRect();
                setInputLocation({
                    top: rect.bottom,
                    left: rect.left,
                    width: rect.width
                });
            }
        };
        window.addEventListener('resize', updateRect);
        updateRect();
        return () => window.removeEventListener('resize', updateRect);
    }, [inFocus]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            // We only trigger if the element is not the input or results
            const target = e.target as Node;
            if (
                ProductSelectorWrapperRef.current && !ProductSelectorWrapperRef.current.contains(target) &&
                ProductSelectorResultsRef.current && !ProductSelectorResultsRef.current.contains(target)
            ) {
                handleOnBlur();
            }
        }

        if (inFocus) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [inFocus]);

    const handleScanBarcode = () => {
        if (!storageId) return
        setBarcodeScanHandler(async (barcode: string) => {
            const mapping = await fetchBarcodeMapping(queryClient, storageId, barcode)
            if (!mapping?.productId) {
                addToast({ text: t('toastBarcodeNotFound'), colorScheme: 'red' })
                return false
            }
            onProductSelect && onProductSelect(mapping.productId)
            setInFocus(false)
            setFilterQuery('')
            closeDialog()
            return true
        })
        openDialog('scan_barcode')
    }

    const selectedItemPreview = selectedProduct ? (
        <Doc
            title={`${selectedProduct.sku}: ${selectedProduct.name}`}
            image={selectedProduct.images && selectedProduct.images.length > 0 && (
                <Image
                    src={selectedProduct.images[0].url} 
                    alt={selectedProduct.name}
                    size="md"
                />
            )}
            direction="horizontal"
            className="mt-2 !p-2 w-fit"
            icon={<CloseIcon />}
            iconOnClick={() => {
                onProductSelect && onProductSelect('');
            }}
        >
            <div><AmountTag amount={Number(selectedProduct.status?.currentAmount)} unit={selectedProduct.unit} lowStockWarning={selectedProduct.stockSettings?.lowStockWarning} /></div>
            {selectedProduct.locations && selectedProduct.locations.length > 0 && <div>{getLocationNamesString(selectedProduct.locations)}</div>}
            {selectedProduct.categories.length > 0 && <div>{getCategoryNamesString(selectedProduct.categories)}</div>}
        </Doc>
    ) : null;

    const ProductSelector = selectedProduct ? null : (
        <div ref={ProductSelectorWrapperRef} className={`relative ${className ? className : ''}`}>
            <div className={`relative w-full`}>
                <SearchIcon 
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${storageId ? `cursor-pointer` : `cursor-not-allowed`} fill-gray-500`}
                    onClick={handleSearchIconClick}
                />
                <input 
                    type={`text`}
                    placeholder={inFocus ? (placeholder || t('placholderSearchQuery')) : ``}
                    className={`
                        rounded-md
                        w-full
                        focus:outline-none
                        px-10
                        ${inFocus ? `border-gray-500 border-b-white rounded-b-none` : ``}
                    `} 
                    value={inFocus ? filterQuery : ``}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    data-ignore={`true`}
                    onFocus={handleOnClick}
                    ref={searchInputRef}
                    disabled={!storageId}
                />
                {inFocus && filterQuery.length > 0 &&
                    <CloseIcon 
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer fill-black`} 
                        onClick={() => setFilterQuery('')}
                    />
                }
            </div>
        </div>
    )

    const results = selectedProduct ? null : inFocus && inputLocation && (
            <SearchResultHolder
                inputRef={inputLocation}
                resultsRef={ProductSelectorResultsRef}
                inline={inlineResults}
            >
                {filterQuery.length && !selectableProducts.length ? <Callout type="error">{t('noResultsFound')}</Callout> : null}

                {selectableProducts.length ? (
                    <TableList className="small-list hover-effect border-none rounded-t-none">
                        <TableListRow isHeader>
                            <TableListCell></TableListCell>
                            <TableListCell>{t('labelSKU')}</TableListCell>
                            <TableListCell>{t('name')}</TableListCell>
                            <TableListCell></TableListCell>
                            {hasLocation && <TableListCell>{t('location')}</TableListCell>}
                            {hasCategories && <TableListCell>{t('labelCategory')}</TableListCell>}
                            {Object.entries(queryMatchingAttributes).map(([categoryId, customInputNames], _key) => {
                                if (categoryId === "_") return null
                                const categoryName = findCategory(categoryId)?.name
                                if (customInputNames.length > 0) {
                                    return customInputNames.map((customInputName, key) => (
                                        <TableListCell key={key} className="capitalize" direction="vertical">
                                            <span className={`text-gray-500`}>{categoryName}</span><span>{customInputName}</span>
                                        </TableListCell>
                                    ))
                                }
                                return null
                            })}
                        </TableListRow>
                        {selectableProducts.slice(0, APP_CONFIG.DEFAULT_SEARCH_LIMIT).map((product, key) => {
                            if (!product.id) return null

                            const handleProductClick = () => {
                                if (onProductSelect && product.id) {
                                    onProductSelect(product.id);
                                    setInFocus(false);
                                    setFilterQuery('');
                                }
                            };

                            const isSelected = selectedProductCode === product.id;

                            return (
                                <TableListRow
                                    key={key}
                                    onClick={handleProductClick}
                                    className={`cursor-pointer ${isSelected ? 'bg-blue-100' : 'hover:bg-gray-100'}`}
                                >
                                    <TableListCell>
                                        {product.images && product.images.length > 0 ? (
                                            <Image 
                                                src={product.images[0].url} 
                                                alt={product.name}
                                                size="sm"
                                            />
                                        ) : <Image size="sm" />}
                                    </TableListCell>
                                    <TableListCell>{product.sku}</TableListCell>
                                    <TableListCell>{product.name}</TableListCell>
                                    <TableListCell>
                                        <AmountTag amount={Number(product.status?.currentAmount)} unit={product.unit} lowStockWarning={product.stockSettings?.lowStockWarning} />
                                    </TableListCell>
                                    {hasLocation && <TableListCell>{getLocationNamesString(product.locations || []) || product.location}</TableListCell>}
                                    {hasCategories && <TableListCell>{getCategoryNamesString(product.categories)}</TableListCell>}
                                    {Object.entries(queryMatchingAttributes).map(([categoryId, customInputNames], _key) => {
                                        if (categoryId === "_" || !product.customInputs) return null
                                        const customInputCategory = product.customInputs[categoryId]
                                        if (customInputNames.length > 0) {
                                            return customInputNames.map((customInputName, key) => {
                                                if (!customInputCategory) return <TableListCell key={key}>-</TableListCell>
                                                return <TableListCell key={key}>{customInputCategory[customInputName]}</TableListCell>
                                            })
                                        } else {
                                            return null
                                        }
                                    })}
                                </TableListRow>
                            )
                        })}
                    </TableList>
                ) : null}
                <LoadMore hasNextPage={hasNextPage} action={fetchNextPage} isLoading={productsStatus === FormStatus.LOADING} trigger={products} />
            </SearchResultHolder>
        )

    return (
        <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
                {/* Search Field */}
                { ProductSelector }
                {/* Search Results */}
                { results }
                {/* Selected Item */}
                { selectedItemPreview }
            </div>
            {!selectedProductCode && (
                <IconButton
                    icon={<BarcodeIcon />}
                    onClick={handleScanBarcode}
                    disabled={!storageId}
                    className="mt-0 shrink-0"
                />
            )}
        </div>
    )
}

export default memo(ProductSelector)

